import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AuthFirebaseFunctionsService,
  MultifactorService,
} from '@principle-theorem/ng-auth';
import {
  CustomValidators,
  OrganisationService,
  PartnerService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  EventableQueries,
  User,
  updateStafferNames,
} from '@principle-theorem/principle-core';
import { OrganisationPermissions } from '@principle-theorem/principle-core/features';
import {
  IAppointment,
  IBrand,
  IPractice,
  IRole,
  IUser,
  MultifactorOverride,
  PartnerCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  filterUndefined,
  find,
  getError,
  multiSwitchMap,
  reduce2DArray,
  snapshot,
  snapshotDefined,
  toTimePeriod,
  where,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { UserInfo } from 'firebase/auth';
import moment from 'moment-timezone';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  of,
  race,
  type Observable,
} from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { type IBrandPracticeAccess } from '../access-selector/access-selector.component';

const WRITE_REQUEST_TIMEOUT = 2000;

type UserAccessChangesFormData = Pick<
  IUser,
  'name' | 'email' | 'phone' | 'roles' | 'brands' | 'practices' | 'isEnabled'
> & {
  multifactorOverride: IUser['multifactorOverride'] | 'inherit';
};

@Component({
  selector: 'pr-manage-user',
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageUserComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  saving$ = new BehaviorSubject<boolean>(false);
  user$ = new ReplaySubject<WithRef<IUser>>(1);
  isLoggedInUser$ = new ReplaySubject<boolean>(1);
  form = new TypedFormGroup<UserAccessChangesFormData>({
    name: new TypedFormControl<string>('', Validators.required),
    email: new TypedFormControl<string>('', [
      Validators.required,
      Validators.email,
    ]),
    phone: new TypedFormControl<string>('', CustomValidators.phoneNumber),
    multifactorOverride: new TypedFormControl(undefined),
    roles: new TypedFormControl<DocumentReference<IRole>[]>([]),
    brands: new TypedFormControl<DocumentReference<IBrand>[]>([]),
    practices: new TypedFormControl<DocumentReference<IPractice>[]>([]),
    isEnabled: new TypedFormControl<boolean>(),
  });
  brands$ = new ReplaySubject<WithRef<IBrand>[]>(1);
  roles$ = new ReplaySubject<WithRef<IRole>[]>(1);
  disabled$ = new BehaviorSubject<boolean>(false);
  canConfigureAuth$: Observable<boolean>;
  @Output() inviteUser = new EventEmitter<WithRef<IUser>>();
  showRoles = true;
  showAccess = true;

  @Input()
  set hideRoles(hideRoles: BooleanInput) {
    this.showRoles = !coerceBooleanProperty(hideRoles);
  }

  @Input()
  set hideAccess(hideAccess: BooleanInput) {
    this.showAccess = !coerceBooleanProperty(hideAccess);
  }

  @Input()
  set isLoggedInUser(isLoggedInUser: boolean) {
    this.isLoggedInUser$.next(isLoggedInUser);
  }

  @Input()
  set user(user: WithRef<IUser>) {
    if (user) {
      this.user$.next(user);
    }
  }

  @Input()
  set brands(brands: WithRef<IBrand>[]) {
    if (brands) {
      this.brands$.next(brands);
    }
  }

  @Input()
  set roles(roles: WithRef<IRole>[]) {
    if (roles) {
      this.roles$.next(roles);
    }
  }

  constructor(
    private _snackBar: MatSnackBar,
    private _functions: AuthFirebaseFunctionsService,
    private _basicDialog: BasicDialogService,
    public organisation: OrganisationService,
    private _partner: PartnerService,
    private _multifactorAdmin: MultifactorService
  ) {
    this.canConfigureAuth$ = this.organisation.userHasPermission$(
      OrganisationPermissions.OrganisationConfigureAuth
    );

    this.user$.pipe(take(1), takeUntil(this._onDestroy$)).subscribe((user) =>
      this.form.patchValue({
        ...user,
        multifactorOverride: user.multifactorOverride || 'inherit',
      })
    );

    this.form.controls.isEnabled.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => {
        if (!value) {
          this.form.patchValue(
            {
              practices: [],
              brands: [],
              roles: [],
            },
            { emitEvent: false }
          );
        }
        this.disabled$.next(!value);
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  toFirebaseUser(user: WithRef<IUser>): UserInfo {
    return User.toFirebaseUser(user);
  }

  canInvite(user: WithRef<IUser>): boolean {
    return user.firstSignedInAt ? false : true;
  }

  updateRoles(roles: DocumentReference<IRole>[]): void {
    this.form.patchValue({ roles });
  }

  updateAccess({ brands, practices }: IBrandPracticeAccess): void {
    this.form.patchValue({
      brands,
      practices,
    });
  }

  async save(): Promise<void> {
    this.saving$.next(true);
    if (!this.form.valid) {
      this.saving$.next(false);
      return;
    }

    const isDisabled = await snapshot(this.disabled$);
    const currentUser = await snapshot(this.user$);
    const isPartnerUser = currentUser.ref.path.includes(
      `/${PartnerCollection.Users}/`
    );

    if (isDisabled && !isPartnerUser) {
      const userAppointments = await snapshot(
        this._userAppointments$(currentUser)
      );

      if (userAppointments.length) {
        const confirmed = await this._basicDialog.confirm({
          title: 'User Has Scheduled Appointments',
          prompt: `${currentUser.name} has ${userAppointments.length} future appointment(s) scheduled. Disabling this user will prevent these from displaying on the Timeline. Proceed?`,
          toolbarColor: 'warn',
          submitColor: 'warn',
        });
        if (!confirmed) {
          this.form.patchValue(currentUser);
          this.saving$.next(false);
          return;
        }
      }
    }

    const { multifactorOverride, ...data } = this.form.getRawValue();
    const userChanges = {
      ...data,
      multifactorOverride:
        multifactorOverride === 'inherit'
          ? undefined
          : multifactorOverride || undefined,
    };
    userChanges.name = userChanges.name.trim();
    const emailChanged = userChanges.email !== currentUser.email;

    if (emailChanged) {
      const emailInUse = await snapshot(this._emailInUse$(userChanges.email));

      if (emailInUse) {
        this.saving$.next(false);
        this._snackBar.open(
          'Sorry that email is already in use! Please use a different email.'
        );
        return;
      }
    }

    const organisation = !isPartnerUser
      ? await snapshotDefined(this.organisation.organisation$)
      : undefined;
    const orgMultifactorEnabled = !isPartnerUser
      ? organisation?.settings?.multifactor?.enabled ?? false
      : false;
    const canConfigureAuth = await snapshot(this.canConfigureAuth$);
    const effectiveMultifactorOverride = canConfigureAuth
      ? userChanges.multifactorOverride
      : currentUser.multifactorOverride;

    if (!isPartnerUser && canConfigureAuth) {
      const oldRequired = this._isMultifactorRequired(
        currentUser.multifactorOverride,
        orgMultifactorEnabled
      );
      const newRequired = this._isMultifactorRequired(
        effectiveMultifactorOverride,
        orgMultifactorEnabled
      );

      if (oldRequired && !newRequired && organisation) {
        try {
          const result =
            await this._multifactorAdmin.disableMultifactorForUsers(
              [currentUser.ref],
              organisation.ref
            );

          if (result.failedUids.length > 0) {
            this.saving$.next(false);
            this._snackBar.open(
              `Failed to disable two-factor authentication for ${result.failedUids.length} user(s). User was not updated.`
            );
            return;
          }
        } catch (error) {
          this.saving$.next(false);
          // eslint-disable-next-line no-console
          console.error(getError(error));
          this._snackBar.open(
            'Could not disable two-factor authentication for user'
          );
          return;
        }
      }
    }

    await Firestore.updateDoc(currentUser.ref, {
      ...userChanges,
      email: userChanges.email.toLowerCase(),
      multifactorOverride: effectiveMultifactorOverride ?? undefined,
    });

    await updateStafferNames({
      ...currentUser,
      ...userChanges,
    });

    this._snackBar.open('User Roles & Access Updated');

    if (emailChanged) {
      const existingFirebaseUser = await this._functions.updateUserEmail(
        currentUser.email,
        userChanges.email
      );

      if (!existingFirebaseUser) {
        const partner = await snapshot(this._partner.partner$);

        if (organisation) {
          await this._functions.sendOrganisationInviteEmail(organisation, {
            email: userChanges.email,
          });
        }

        if (partner) {
          await this._functions.sendPartnerInviteEmail(partner, {
            email: userChanges.email,
          });
        }

        setTimeout(() => {
          this._snackBar.open(
            `Email address must be verified. Please check ${userChanges.email} for activation link.`
          );
        }, WRITE_REQUEST_TIMEOUT);
      }
    }
    this.saving$.next(false);
  }

  private _userAppointments$(
    user: WithRef<IUser>
  ): Observable<WithRef<IAppointment>[]> {
    const range = toTimePeriod(moment(), moment().add(18, 'months'));

    return this.brands$.pipe(
      multiSwitchMap((brand) =>
        Brand.getBrandStafferFromUser(brand, user).pipe(
          switchMap((staffer) =>
            staffer
              ? EventableQueries.appointments$(brand, range, [staffer.ref])
              : of([])
          )
        )
      ),
      reduce2DArray()
    );
  }

  private _emailInUse$(email: string): Observable<boolean> {
    const organisationUserExists$ = this.organisation.userCol$.pipe(
      filterUndefined(),
      find(where('email', '==', email.toLowerCase())),
      map((user: WithRef<IUser> | undefined) => (user ? true : false))
    );

    const partnerUserExists$ = this._partner.userCol$.pipe(
      filterUndefined(),
      find(where('email', '==', email.toLowerCase())),
      map((user: WithRef<IUser> | undefined) => (user ? true : false))
    );

    return race(organisationUserExists$, partnerUserExists$);
  }

  private _isMultifactorRequired(
    override: UserAccessChangesFormData['multifactorOverride'],
    orgEnabled: boolean
  ): boolean {
    if (override === MultifactorOverride.Required) {
      return true;
    }
    if (orgEnabled && override !== MultifactorOverride.Disabled) {
      return true;
    }
    return false;
  }
}
